// §9.1 — in-process apps.
//
// A built-in is mounted into its own `.win-host` element and gets the same `HangarAPI` an
// external app reaches over the bridge (D-006) — one instance per window, bound to that
// window's app id. It may not import kernel modules (§6.5); everything it can do, it does
// through the api it is handed.
// The `InProcessApp` contract itself lives in `shared` (§9.1): `apps/**` may import nothing
// else (§6.5), and a built-in has to be able to name the type it implements.
import type {
  AppInstanceContext,
  HangarAPI,
  InProcessApp,
  InProcessFileType,
  JumpListEntry,
  Win,
} from '@hangar/shared';

export type { InProcessApp, InProcessWindowDefaults, InProcessFileType } from '@hangar/shared';
export { DEFAULT_WINDOW, windowDefaults } from '@hangar/shared';

interface MountedInstance {
  appId: string;
  container: HTMLElement;
  api: HangarAPI;
  context: AppInstanceContext;
}

export interface AppMountService {
  register(app: InProcessApp): void;
  /** Every registered app id, gate or no gate. Internal callers only. */
  registered(): string[];
  /**
   * §24.11 (D-200) — the ids a *user* may see, filtered by `requiresAuth` against the current
   * identity. **This is the list every UI surface reads** — launcher, §12 search, the bar, the
   * Store and §5.6 restore — so that "which apps exist" has one answer that respects the gate.
   * `get()` deliberately still resolves a gated app, because internal callers need it.
   */
  visible(): string[];
  get(appId: string): InProcessApp | undefined;
  /** Mounts the app for `win` into `container`. A second call for the same window is a no-op. */
  mount(win: Win, container: HTMLElement): boolean;
  /**
   * Tears the instance down. Called when — and only when — the window closes (§5.7).
   *
   * **The teardown is deferred by a microtask, and a re-mount of the same window cancels it**
   * (D-125). A built-in owns a React root inside its container, and this is called from the host
   * layer's ref cleanup — which runs inside the *parent* root's commit. Unmounting a nested root
   * there is what React forbids, and under `StrictMode` it is not even a real close: React
   * detaches and immediately re-attaches every ref to check for exactly this kind of bug.
   */
  unmount(instanceKey: string): void;
  /** The api handed to that window's app, for tests and App Inspector. */
  apiFor(instanceKey: string): HangarAPI | undefined;
  mountedKeys(): string[];
  /** §9.2 — the app's declared taskbar actions, or an empty list (D-100). */
  jumpListOf(appId: string): readonly JumpListEntry[];
  /**
   * §9.3 `command`/`fire` — deliver a chosen jump-list entry or menu item to one window's app.
   * Returns false when that window is not mounted or its app does not take commands.
   */
  fireCommand(instanceKey: string, commandId: string): boolean;
}

export interface AppMountOptions {
  /**
   * Builds the api for one window. Injected rather than imported: `kernel/api` reaches this
   * service through the launch service, so importing it back would make the two a cycle
   * (§4.2).
   */
  createApi: (ctx: AppInstanceContext) => HangarAPI;
  /** Reported when a built-in throws out of `mount`; the window stays, empty (F12-shaped). */
  onMountError?: (appId: string, error: unknown) => void;
  /** §9.2 — a registered app's declared file types, handed to the registry (M5.1). */
  onFileTypes?: (appId: string, types: readonly InProcessFileType[]) => void;
  /** How a deferred teardown is scheduled. Injected so a test can run it on demand (D-125). */
  defer?: (fn: () => void) => void;
  /**
   * §24.11 — whether anyone is signed in. Read per call rather than captured, so signing in or
   * out changes what `visible()` answers in the same tick, with nothing to invalidate.
   */
  isSignedIn?: () => boolean;
}

export function contextFor(win: Win): AppInstanceContext {
  return { appId: win.appId, winId: win.id, instanceKey: win.instanceKey };
}

export function createAppMountService(options: AppMountOptions): AppMountService {
  const apps = new Map<string, InProcessApp>();
  const mounted = new Map<string, MountedInstance>();
  /** Instances whose teardown is scheduled but has not run. A re-mount cancels it (D-125). */
  const closing = new Set<string>();
  const defer = options.defer ?? ((fn: () => void) => queueMicrotask(fn));

  return {
    register(app) {
      if (apps.has(app.id)) throw new Error(`in-process app '${app.id}' is already registered`);
      apps.set(app.id, app);
      // §9.2 — a built-in's `fileTypes` reach the registry the same way an installed app's
      // manifest does, so "Open with" cannot tell the two apart.
      if (app.fileTypes) options.onFileTypes?.(app.id, app.fileTypes);
    },

    registered() {
      return [...apps.keys()];
    },

    visible() {
      const signedIn = options.isSignedIn?.() ?? false;
      return [...apps.values()]
        .filter((app) => app.requiresAuth !== true || signedIn)
        .map((app) => app.id);
    },

    get(appId) {
      return apps.get(appId);
    },

    mount(win, container) {
      // A re-attach arriving before the deferred teardown ran: the instance is still live and
      // still owns this very container, so adopt it rather than building a second React root on
      // a node that already has one (D-125).
      const closingInstance = closing.has(win.instanceKey) ? mounted.get(win.instanceKey) : undefined;
      if (closingInstance && closingInstance.container === container) {
        closing.delete(win.instanceKey);
        return true;
      }

      if (mounted.has(win.instanceKey)) return true;
      const app = apps.get(win.appId);
      if (!app) return false;

      const context = contextFor(win);
      const api = options.createApi(context);
      mounted.set(win.instanceKey, { appId: win.appId, container, api, context });
      try {
        app.mount(container, api);
      } catch (error) {
        // A built-in that fails to mount leaves an empty window rather than taking the
        // desktop down with it.
        mounted.delete(win.instanceKey);
        options.onMountError?.(win.appId, error);
        return false;
      }
      return true;
    },

    unmount(instanceKey) {
      const instance = mounted.get(instanceKey);
      if (!instance || closing.has(instanceKey)) return;
      closing.add(instanceKey);

      defer(() => {
        // Cancelled: `mount` adopted this instance again before the microtask ran.
        if (!closing.delete(instanceKey)) return;
        if (mounted.get(instanceKey) !== instance) return;
        mounted.delete(instanceKey);
        const app = apps.get(instance.appId);
        try {
          app?.unmount(instance.container);
        } catch (error) {
          options.onMountError?.(instance.appId, error);
        }
      });
    },

    apiFor(instanceKey) {
      return mounted.get(instanceKey)?.api;
    },

    mountedKeys() {
      return [...mounted.keys()];
    },

    jumpListOf(appId) {
      return apps.get(appId)?.jumpList ?? [];
    },

    fireCommand(instanceKey, commandId) {
      const instance = mounted.get(instanceKey);
      if (!instance) return false;
      const app = apps.get(instance.appId);
      if (!app?.onCommand) return false;
      try {
        app.onCommand(commandId, instance.api, instance.context);
      } catch (error) {
        // A jump-list entry that throws is the app's problem, never the taskbar's.
        options.onMountError?.(instance.appId, error);
        return false;
      }
      return true;
    },
  };
}
